import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient, HeroBanner } from '../lib/api';
import { getAssetUrl } from '../lib/utils';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import { DataTable } from './common/DataTable';
import {
    Image as ImageIcon,
    Upload,
    Pencil,
    Trash2,
    X,
    Plus,
    Search,
    RefreshCw,
    ToggleLeft,
    ToggleRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';

const HeroBannersPage: React.FC = () => {
    const [banners, setBanners] = useState<HeroBanner[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingBanner, setEditingBanner] = useState<HeroBanner | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState<string>('all');

    const [formData, setFormData] = useState({
        image: '',
        link: '/',
        title: '',
        subtitle: '',
        order: 0,
        isActive: true,
        badge: '',
        primaryCTALabel: '',
        primaryCTALink: '',
        secondaryCTALabel: '',
        secondaryCTALink: ''
    });

    const getImageUrl = (path: string) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return getAssetUrl(path);
    };

    const fetchBanners = useCallback(async () => {
        try {
            setLoading(true);
            const res = await adminApiClient.getHeroBanners({
                limit: 100,
                ...(searchTerm && { search: searchTerm }),
                ...(activeFilter !== 'all' && { isActive: activeFilter })
            });
            if (res.success && res.data?.banners) {
                setBanners(res.data.banners);
            } else {
                toast.error(res.message || 'Failed to fetch banners');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error fetching banners');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, activeFilter]);

    useEffect(() => {
        fetchBanners();
    }, [fetchBanners]);

    const resetForm = () => {
        setFormData({
            image: '',
            link: '/',
            title: '',
            subtitle: '',
            order: banners.length,
            isActive: true,
            badge: '',
            primaryCTALabel: '',
            primaryCTALink: '',
            secondaryCTALabel: '',
            secondaryCTALink: ''
        });
        setEditingBanner(null);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setUploading(true);
            const res = await adminApiClient.uploadFile('website-config', file);
            if (res.success && res.data?.path) {
                const path = res.data.path;
                setFormData(prev => ({ ...prev, image: path.startsWith('/') ? path : `/${path}` }));
                toast.success('Image uploaded');
            } else {
                toast.error(res.message || 'Upload failed');
            }
        } catch (err: any) {
            toast.error(err.message || 'Upload failed');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const toastId = toast.loading('Saving...');
        try {
            let res;
            if (editingBanner) {
                res = await adminApiClient.updateHeroBanner(editingBanner._id, formData);
            } else {
                res = await adminApiClient.createHeroBanner(formData);
            }
            if (res.success) {
                toast.success('Saved successfully', { id: toastId });
                fetchBanners();
                setShowForm(false);
                resetForm();
            } else {
                toast.error(res.message || 'Failed to save', { id: toastId });
            }
        } catch (err: any) {
            toast.error(err.message || 'Error saving', { id: toastId });
        }
    };

    const handleEdit = (banner: HeroBanner) => {
        setEditingBanner(banner);
        setFormData({
            image: banner.image || '',
            link: banner.link || '/',
            title: banner.title || '',
            subtitle: banner.subtitle || '',
            order: banner.order ?? 0,
            isActive: banner.isActive !== false,
            badge: banner.badge || '',
            primaryCTALabel: banner.primaryCTALabel || '',
            primaryCTALink: banner.primaryCTALink || '',
            secondaryCTALabel: banner.secondaryCTALabel || '',
            secondaryCTALink: banner.secondaryCTALink || ''
        });
        setShowForm(true);
    };

    const handleToggle = async (id: string) => {
        try {
            const res = await adminApiClient.toggleHeroBanner(id);
            if (res.success) {
                toast.success(res.message || 'Updated');
                fetchBanners();
            } else {
                toast.error(res.message || 'Failed');
            }
        } catch (err: any) {
            toast.error(err.message || 'Error');
        }
    };

    const handleDelete = async (id: string) => {
        const ok = await showDeleteConfirmation('Delete Banner?', 'This cannot be undone.');
        if (!ok) return;
        try {
            const res = await adminApiClient.deleteHeroBanner(id);
            if (res.success) {
                toast.success('Deleted');
                fetchBanners();
            } else {
                toast.error(res.message || 'Failed to delete');
            }
        } catch (err: any) {
            toast.error(err.message);
        }
    };

    const columns: ColumnDef<HeroBanner>[] = [
        {
            accessorKey: 'image',
            header: 'Image',
            cell: (info) => (
                <div className="w-16 h-12 rounded-lg bg-gray-100 overflow-hidden border border-gray-200">
                    {info.getValue<string>() ? (
                        <img src={getImageUrl(info.getValue<string>())} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <div className="flex items-center justify-center w-full h-full text-gray-400">
                            <ImageIcon size={20} />
                        </div>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: (info) => (
                <div>
                    <div className="font-medium text-gray-900">{info.getValue<string>() || '-'}</div>
                    <div className="text-xs text-gray-500 line-clamp-1 max-w-xs">{info.row.original.subtitle}</div>
                </div>
            )
        },
        {
            accessorKey: 'link',
            header: 'Link',
            cell: (info) => (
                <span className="text-sm text-gray-600 font-mono truncate max-w-[140px] block" title={info.getValue<string>()}>
                    {info.getValue<string>() || '/'}
                </span>
            )
        },
        {
            accessorKey: 'order',
            header: 'Order',
            cell: (info) => <span className="text-sm text-gray-600">{info.getValue<number>() ?? 0}</span>
        },
        {
            accessorKey: 'isActive',
            header: 'Status',
            cell: (info) => {
                const isActive = info.getValue<boolean>();
                return (
                    <button
                        onClick={() => handleToggle(info.row.original._id)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                            isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title={isActive ? 'Click to disable' : 'Click to enable'}
                    >
                        {isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                        {isActive ? 'Enabled' : 'Disabled'}
                    </button>
                );
            }
        },
        {
            accessorKey: 'createdAt',
            header: 'Created',
            cell: (info) => (
                <span className="text-xs text-gray-500">
                    {info.getValue<string>() ? format(new Date(info.getValue<string>()), 'MMM d, yyyy') : '-'}
                </span>
            )
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (info) => (
                <div className="flex gap-2">
                    <button onClick={() => handleEdit(info.row.original)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                        <Pencil size={18} />
                    </button>
                    <button onClick={() => handleDelete(info.row.original._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Hero Banners</h1>
                    <p className="text-sm text-gray-500">Manage homepage hero slider banners</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={fetchBanners} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Refresh">
                        <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={() => { resetForm(); setShowForm(true); }}
                        className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors shadow-sm"
                    >
                        <Plus size={20} /> Add Banner
                    </button>
                </div>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-gray-900">{editingBanner ? 'Edit Banner' : 'New Banner'}</h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 max-h-[85vh] overflow-y-auto">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-32 h-20 rounded-lg bg-gray-100 border flex items-center justify-center overflow-hidden">
                                        {formData.image ? (
                                            <img src={getImageUrl(formData.image)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <ImageIcon className="text-gray-400" size={32} />
                                        )}
                                    </div>
                                    <div>
                                        <label className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                                            <Upload className="-ml-1 mr-2 h-4 w-4" />
                                            {uploading ? 'Uploading...' : 'Upload'}
                                            <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="Banner title" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                                    <input type="text" value={formData.subtitle} onChange={e => setFormData({ ...formData, subtitle: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="Optional (legacy)" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Badge</label>
                                    <input type="text" value={formData.badge} onChange={e => setFormData({ ...formData, badge: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="e.g. Divine, Featured" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Primary CTA Label</label>
                                        <input type="text" value={formData.primaryCTALabel} onChange={e => setFormData({ ...formData, primaryCTALabel: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="e.g. View Poojas" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Primary CTA Link</label>
                                        <input type="text" value={formData.primaryCTALink} onChange={e => setFormData({ ...formData, primaryCTALink: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="/poojas" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Secondary CTA Label</label>
                                        <input type="text" value={formData.secondaryCTALabel} onChange={e => setFormData({ ...formData, secondaryCTALabel: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="e.g. Learn More" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Secondary CTA Link</label>
                                        <input type="text" value={formData.secondaryCTALink} onChange={e => setFormData({ ...formData, secondaryCTALink: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="/about" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
                                    <input type="text" value={formData.link} onChange={e => setFormData({ ...formData, link: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none" placeholder="/temples, /poojas, /events, /sponsorships" />
                                    <p className="text-xs text-gray-500 mt-1">Used as primary CTA link when Primary CTA Link is empty.</p>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                                        <input type="number" value={formData.order} onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                                    </div>
                                    <div className="flex items-end pb-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} className="rounded text-orange-600 focus:ring-orange-500" />
                                            <span className="text-sm text-gray-700">Enabled</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-6 flex justify-end gap-3 border-t mt-6">
                                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                                <button type="submit" className="px-4 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700">Save Banner</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search by title, subtitle, link..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
                    />
                </div>
                <div>
                    <select
                        value={activeFilter}
                        onChange={e => setActiveFilter(e.target.value)}
                        className="px-4 py-2 border rounded-lg text-sm bg-white min-w-[140px]"
                    >
                        <option value="all">All</option>
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                    </select>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <DataTable
                    data={banners}
                    columns={columns}
                    loading={loading}
                    enableClientSidePagination={true}
                />
            </div>
        </div>
    );
};

export default HeroBannersPage;
